import React, { useState } from 'react';
import { Table } from 'react-bootstrap';
import PropTypes from 'prop-types';
import './styles.css';

const AnticipationTable = ({ title, orders, tags, utility, boldSeparatorColumns = [], onToggleProductNames = null }) => {

  // Tri des tags selon le champ sort pour les mettre dans le bon ordre de colonne
  const sortedTags = [...tags].sort((a, b) => {
    const sortA = a.sort ?? 0;
    const sortB = b.sort ?? 0;
    return sortA - sortB;
  });

  // Calcul des totaux
  const calculateTotals = () => {
    const totals = { Autres: [] };
    sortedTags.forEach(tag => {
      totals[tag.name] = [];
    });

    orders.forEach(order => {
      const products = order.tagProducts[utility] || {};
      Object.entries(products).forEach(([tagName, tagProducts]) => {
        if (Array.isArray(tagProducts)) {
          tagProducts.forEach(product => {
            // Exclure les produits "Spécial" uniquement dans la colonne "Autres"
            if (tagName === 'Autres' && product.prodCategory === 'Spécial') {
              return;
            }
            const existingProduct = totals[tagName]?.find(p => 
              p.genericId === product.genericId
            );
            if (existingProduct) {
              existingProduct.quantity += parseFloat(product.quantity) || 0;
            } else if (product.genericId) {
              totals[tagName]?.push({ ...product });
            }
          });
        }
      });

      // Gestion des "Autres"
      const autresProducts = order.tagProducts.autres?.Autres || [];
      if (Array.isArray(autresProducts)) {
        autresProducts.forEach(product => {
          // Exclure les produits "Spécial" dans la colonne "Autres"
          if (product.prodCategory === 'Spécial') {
            return;
          }
          const existingProduct = totals.Autres.find(p => 
            p.genericId === product.genericId
          );
          if (existingProduct) {
            existingProduct.quantity += parseFloat(product.quantity) || 0;
          } else if (product.genericId) {
            totals.Autres.push({ ...product });
          }
        });
      }
    });

    return totals;
  };

  const totals = calculateTotals();

  // Détermine si un produit doit afficher son nom
  const shouldShowProductName = (tagName, products) => {
    // Si c'est la colonne Café
    if (!Array.isArray(products)) return false;
    
    // Si plusieurs produits différents dans la même colonne
    const uniqueProductIds = new Set(products.map(p => p.genericId));
    if (uniqueProductIds.size > 1) return true;
    
    // Si le produit apparaît dans plusieurs colonnes
    const productAppearances = new Set();
    orders.forEach(order => {
      const tagProducts = order.tagProducts[utility]?.[tagName];
      if (Array.isArray(tagProducts)) {
        tagProducts.forEach(product => {
          if (product.genericId) {
            productAppearances.add(product.genericId);
          }
        });
      }
    });
    
    return productAppearances.size > 1;
  };

  // Colonnes du tableau
  const columns = [
    { header: "H départ", key: "roadmapStartHour" },
    ...(title === "Tableau des boissons PR/TR" ? [{ header: "Nb pers.", key: "participantsNumber" }] : []),
    { header: "Presta", key: "orderNumber" },
    ...sortedTags.map(tag => ({
      header: tag.name,
      key: tag.name
    })),
    { header: "Autres", key: "Autres" },
    ...(title === "Tableau des boissons" ? [{ header: "Tournée", key: "roadmapLetter" }] : [])
  ];

  const renderProductCell = (order, products, tagName) => {
    if (!Array.isArray(products)) return '';
    
    // Pour toutes les anticipations, comportement normal
    const content = products
      .filter(product => {
        if (!product || !product.genericId) return false;
        // Exclure les produits "Spécial" uniquement dans la colonne "Autres"
        if (tagName === 'Autres' && product.prodCategory === 'Spécial') {
          return false;
        }
        return true;
      })
      .map(product => {
        // Nom raccourci pour les fiches anticipation (logistique) : champ
        // `proAnticipationName` sur le générique (fallback nom complet côté API).
        // Exception : les product parts gardent leur nom de part (productName).
        const displayName = product.isProductPart
          ? product.productName
          : (product.proAnticipationName || product.productName);
        if (tagName === 'Autres' || tagName === 'Sablés beurre') {
          return `<span>${displayName} - ${product.quantity}${product.unit ? ` ${product.unit}` : ''}</span>`;
        }
        const mustAlwaysShowProductName = tagName === 'Matériel';
        const formattedProduct = (mustAlwaysShowProductName || shouldShowProductName(tagName, products))
          ? `<span>${displayName} - ${product.quantity}${product.unit ? ` ${product.unit}` : ''}</span>`
          : `<span>${product.quantity}${product.unit ? ` ${product.unit}` : ''}</span>`;

        return formattedProduct;
      })
      .filter(Boolean)
      .join(' || ');

    const shouldShowNomadWarning = title === "Tableau des boissons PR/TR" && order?.hasNomadFormula;

    if (!content) {
      return '';
    }

    return shouldShowNomadWarning
      ? `${content}<br/><span style="font-weight:bold;color:#c0392b;font-size:0.85em;font-style:italic;">Formule nomade / À contrôler</span>`
      : content;
  };

  // Vérifie si une commande contient des produits dans les colonnes concernées
  const hasProducts = (order) => {
    // Vérifie les produits dans les colonnes de tags
    const hasTagProducts = sortedTags.some(tag => {
      const products = order.tagProducts[utility]?.[tag.name];
      return Array.isArray(products) && products.length > 0;
    });

    // Vérifie les produits dans la colonne "Autres"
    const hasAutresProducts = Array.isArray(order.tagProducts.autres?.Autres) && 
                            order.tagProducts.autres.Autres.length > 0;

    return hasTagProducts || hasAutresProducts;
  };

  // Filtrer les commandes qui ont des produits
  const filteredOrders = orders.filter(hasProducts);

  // Calcul du total des participants uniquement pour le tableau des boissons PR/TR
  const totalParticipants = title === "Tableau des boissons PR/TR" 
    ? filteredOrders.reduce((sum, order) => sum + (order?.participantsNumber || 0), 0)
    : 0;

  return (
    <div className="anticipation-table">
      {onToggleProductNames && (
        <div className="no-print mb-2">
          <input
            type="checkbox"
            onChange={(e) => onToggleProductNames(e.target.checked)}
          />
          <label className="ml-2">Afficher le nom des produits</label>
        </div>
      )}
      
      <Table bordered className="mb-4">
        <thead>
          <tr>
            <th colSpan={columns.length} className="text-center">
              {title}
              {title === "Tableau des pièces Sucrées" && (
                <div className="text-right">
                  <div style={{ display: "flex", justifyContent: "flex-end", fontSize: "12px", gap: "15px" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          backgroundColor: "green",
                          width: "15px",
                          height: "15px",
                          marginRight: "5px",
                        }}
                      />
                      <span>0 déchet + service</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          backgroundColor: "orange",
                          width: "15px",
                          height: "15px",
                          marginRight: "5px",
                        }}
                      />
                      <span>0 déchet</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          backgroundColor: "violet",
                          width: "15px",
                          height: "15px",
                          marginRight: "5px",
                        }}
                      />
                      <span>Avec service</span>
                    </div>
                  </div>
                </div>
              )}
              {(title === "Tableau des boissons" || title === "Tableau des pièces Sucrées" || title === "Tableau des boissons PR/TR") && (
                <div className="text-right">
                  <div style={{ display: "flex", justifyContent: "flex-end", fontSize: "12px", gap: "15px", marginTop: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          backgroundColor: "#2b7ed6",
                          width: "15px",
                          height: "15px",
                          marginRight: "5px",
                        }}
                      />
                      <span>PR (Plateau repas)</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          backgroundColor: "black",
                          width: "15px",
                          height: "15px",
                          marginRight: "5px",
                        }}
                      />
                      <span style={{ color: "black" }}>TR (Traiteur)</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          backgroundColor: "#439b5c",
                          width: "15px",
                          height: "15px",
                          marginRight: "5px",
                        }}
                      />
                      <span>PDJ (Petit déjeuner)</span>
                    </div>
                  </div>
                </div>
              )}
              {title === "Tableau des boissons PR/TR" && (
                <div className="text-right">
                  <div style={{ display: "flex", justifyContent: "flex-end", fontSize: "12px", gap: "15px" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        Evian / Badoit = Décapsuleur<br/>
                        Vin = Tire-bouchon
                    </div>
                  </div>
                </div>
              )}
            </th>
          </tr>
          <tr>
            {columns.map((column, index) => (
              <th 
                key={column.key}
                className={boldSeparatorColumns.includes(column.header) ? 'border-right-bold' : ''}
                style={{ width: index === 0 ? '10%' : undefined }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredOrders.map((order, orderIndex) => (
            <tr 
              key={orderIndex}
              style={
                title === "Tableau des pièces Sucrées" 
                ? (order.isZeroWaste && order.withService
                    ? { backgroundColor: "green" }
                    : order.isZeroWaste
                    ? { backgroundColor: "orange" }
                    : order.withService
                    ? { backgroundColor: "violet" }
                    : {})
                : {}
              }
            >
              <td>{order.roadmapStartHour || ''}</td>
              {title === "Tableau des boissons PR/TR" && (
                <td>{order?.participantsNumber || ''}</td>
              )}
              <td className={boldSeparatorColumns.includes('Presta') ? 'border-right-bold' : ''}>
                <span style={{ 
                  padding: '5px', 
                  fontWeight: 'bold', 
                  fontSize: '1.1em', 
                  backgroundColor: order.orderType === 'mealtray' ? '#2b7ed6' 
                    : order.orderType === 'caterer' ? 'black' 
                    : order.orderType === 'breakfast' ? '#439b5c'
                    : 'yellow',
                  color: order.orderType === 'caterer' ? 'white' : 'black'
                }}>
                  {order.orderNumber}
                </span>
              </td>
              {sortedTags.map(tag => (
                <td 
                  key={tag.name}
                  className={boldSeparatorColumns.includes(tag.name) ? 'border-right-bold' : ''}
                  dangerouslySetInnerHTML={{
                    __html: renderProductCell(order, order.tagProducts[utility]?.[tag.name], tag.name)
                  }}
                />
              ))}
              <td
                dangerouslySetInnerHTML={{
                  __html: renderProductCell(order, order.tagProducts[utility]?.Autres, 'Autres')
                }}
              />
              {title === "Tableau des boissons" && (
                <td>{order.roadmapLetter || ''}</td>
              )}
            </tr>
          ))}
          {/* Ligne des totaux */}
          <tr style={{ fontWeight: 'bold', backgroundColor: '#f2f2f2' }}>
            <td>Total</td>
            {title === "Tableau des boissons PR/TR" && (
              <td>{totalParticipants}</td>
            )}
            <td className={boldSeparatorColumns.includes('Presta') ? 'border-right-bold' : ''}></td>
            {sortedTags.map(tag => (
              <td 
                key={tag.name}
                className={boldSeparatorColumns.includes(tag.name) ? 'border-right-bold' : ''}
                dangerouslySetInnerHTML={{
                  __html: renderProductCell(null, totals[tag.name], tag.name)
                }}
              />
            ))}
            <td
              dangerouslySetInnerHTML={{
                __html: renderProductCell(null, totals.Autres, 'Autres')
              }}
            />
            {title === "Tableau des boissons" && (
              <td></td>
            )}
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

AnticipationTable.propTypes = {
  title: PropTypes.string.isRequired,
  orders: PropTypes.array.isRequired,
  tags: PropTypes.array.isRequired,
  utility: PropTypes.string.isRequired,
  boldSeparatorColumns: PropTypes.arrayOf(PropTypes.string),
  onToggleProductNames: PropTypes.func
};

export default AnticipationTable; 